// web/src/pages/admin/EmailLogPage.tsx
//
// Task 12 — Admin Email Log page (`/api/admin/email-log`).
//
// Mirrors the PaymentsListPage chrome pattern: ListPage `search` + single-
// select `filterChips` (All / Sent / Failed) drive backend `q` / `succeeded`
// params; DataGrid pagination is server-side via `page`/`pageSize`. Row
// click opens the shared EmailMessageViewerDialog; this admin page wires
// `onResend` so a "Resend (body only)" button appears for failed messages.
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { emailLogApi, type EmailLogListItem } from "@/lib/email-log";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { EmailMessageViewerDialog } from "@/components/email/EmailMessageViewerDialog";
import { toast } from "@/hooks/use-toast";

type ViewChipId = "all" | "sent" | "failed";

const VIEW_CHIP_OPTIONS: ReadonlyArray<{ id: ViewChipId; label: string }> = [
  { id: "all", label: "All" },
  { id: "sent", label: "Sent" },
  { id: "failed", label: "Failed" },
];

function succeededFor(view: ViewChipId): boolean | undefined {
  switch (view) {
    case "sent":
      return true;
    case "failed":
      return false;
    case "all":
    default:
      return undefined;
  }
}

const PAGE_SIZE = 50;

export function EmailLogPage() {
  const qc = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [selectedView, setSelectedView] = useState<ViewChipId>("all");
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);

  const succeeded = useMemo(() => succeededFor(selectedView), [selectedView]);

  const { data, isLoading } = useQuery({
    queryKey: ["email-log-list", { q: searchText, succeeded, page }],
    queryFn: () =>
      emailLogApi.list({ q: searchText || undefined, succeeded, page, pageSize: PAGE_SIZE }),
  });

  const cols: DataGridColumn<EmailLogListItem>[] = [
    {
      id: "sentAt",
      header: "Sent",
      accessor: (r) => new Date(r.sentAtUtc).toLocaleString("en-AU"),
    },
    { id: "to", header: "To", accessor: (r) => r.toEmailAddress },
    {
      id: "subject",
      header: "Subject",
      accessor: (r) => (
        <button
          type="button"
          className="text-left underline-offset-2 hover:underline"
          onClick={() => setOpenId(r.emailLogId)}
        >
          {r.subject}
        </button>
      ),
    },
    { id: "kind", header: "Kind", accessor: (r) => r.kind },
    {
      id: "attachedTo",
      header: "Attached to",
      accessor: (r) => (r.relatedEntityType ? `${r.relatedEntityType} #${r.relatedEntityId}` : "—"),
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) =>
        r.sentSuccessfully ? (
          <Badge variant="success">Sent</Badge>
        ) : (
          <Badge variant="destructive">Failed</Badge>
        ),
    },
  ];

  return (
    <ListPage backTo="/admin"
      title="Email log"
      search={{
        value: searchText,
        onChange: (v) => {
          setSearchText(v);
          setPage(0);
        },
        placeholder: "Search email log…",
      }}
      filterChips={{
        options: VIEW_CHIP_OPTIONS,
        value: selectedView,
        onChange: (next) => {
          setSelectedView(next);
          setPage(0);
        },
        "aria-label": "Email log status filter",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.emailLogId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        emptyHeadline="No emails match these filters."
        onPageChange={setPage}
      />

      <EmailMessageViewerDialog
        emailLogId={openId}
        open={openId != null}
        onOpenChange={(o) => {
          if (!o) setOpenId(null);
        }}
        onResend={async (id) => {
          await emailLogApi.resend(id);
          toast.success("Email resent.");
          void qc.invalidateQueries({ queryKey: ["email-log-list"] });
          void qc.invalidateQueries({ queryKey: ["email-log", id] });
        }}
      />
    </ListPage>
  );
}
